import React, { createContext, FC, useContext, useEffect, useState } from "react";
import { RegionSetStatusType } from "@app/projects/enumerators";
import {
  ClaimsInsightsRankingRequestTypeEnum,
  ClaimsInsightsScoreTypeEnum,
} from "@app/projects/network-assessment/pci-dashboard/claims-insights";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { cloneDeep } from "lodash";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ClaimsInsightsProviderScenarioResult } from "@app/projects/network-assessment/pci-dashboard/claims-insights/ClaimsInsightsProviderScenarioResult";
import { ClaimsInsightsScore } from "@app/projects/network-assessment/pci-dashboard/claims-insights/ClaimsInsightsScore";
import { ClaimsInsightsScoreSummaryModel } from "@app/projects/network-assessment/pci-dashboard/claims-insights/ClaimsInsightsScoreSummaryModel";
import { ClaimsInsightsSummaryScenarioModel } from "@app/projects/network-assessment/pci-dashboard/claims-insights/ClaimsInsightsSummaryScenarioModel";
import { PciDashboardSummaryType, PciDashboardScenarioTypeEnum, PciDashboardCounty } from "@app/projects/network-assessment/pci-dashboard/shared";
import { PciDashboardFiltersContext } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersContext";
import { PciDashboardFiltersPayload } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersPayload";
import PciDashboardService from "@app/projects/network-assessment/pci-dashboard/shared/pciDashboardService";

export interface ClaimsInsightsDashboardContextValues {
  countyScenarioSummariesLoading: boolean;
  countyScenarioSummariesModel: ClaimsInsightsSummaryScenarioModel;
  countySummariesLoading: boolean;
  countySummariesModel: ClaimsInsightsScoreSummaryModel;
  getScenarioSummaries: (
    filtersPayload: PciDashboardFiltersPayload,
    summaryType: PciDashboardSummaryType,
    sortedColumn: string,
    sortDirection: SortDirection,
    isSorting?: boolean,
    isFiltering?: boolean,
  ) => void;
  getSummaries: (
    filtersPayload: PciDashboardFiltersPayload,
    summaryType: PciDashboardSummaryType,
    sortedColumn: string,
    sortDirection: SortDirection,
    isSorting?: boolean,
    isFiltering?: boolean,
  ) => void;
  marketProvidersEffectivenessLoading: boolean;
  marketProvidersEfficiencyLoading: boolean;
  marketProvidersScores: ClaimsInsightsScore[];
  networkProviderScenarioEffectivenessScores: ClaimsInsightsProviderScenarioResult[];
  networkProviderScenarioEffectivenessScoresLoading: boolean;
  networkProviderScenarioEfficiencyScores: ClaimsInsightsProviderScenarioResult[];
  networkProviderScenarioEfficiencyScoresLoading: boolean;
  networkProvidersScores: ClaimsInsightsScore[];
  networkProvidersEffectivenessLoading: boolean;
  networkProvidersEfficiencyLoading: boolean;
  onApplyFiltersClick: (filtersPayload: PciDashboardFiltersPayload) => Promise<void>;
  onDashboardDetailsViewClick: (
    scoreType: ClaimsInsightsScoreTypeEnum,
    providerNetworkType: ProviderNetworkTypeEnum,
    rankingRequestType: ClaimsInsightsRankingRequestTypeEnum,
    scenario?: PciDashboardScenarioTypeEnum
  ) => void;
  onSummaryDashboardDetailsViewClick: (
    dashboardDetailsParam: string,
    summaryType: PciDashboardSummaryType,
    scenario?: PciDashboardScenarioTypeEnum
  ) => void;
  prospectProvidersScores: ClaimsInsightsScore[];
  prospectProvidersEffectivenessLoading: boolean;
  prospectProvidersEfficiencyLoading: boolean;
  setShowDashboardDetails: (showDashboardDetails: boolean) => void;
  showDashboardDetails: boolean;
  specialtyScenarioSummariesLoading: boolean;
  specialtyScenarioSummariesModel: ClaimsInsightsSummaryScenarioModel;
  specialtySummariesLoading: boolean;
  specialtySummariesModel: ClaimsInsightsScoreSummaryModel;
}

const initialValues: ClaimsInsightsDashboardContextValues = {
  countyScenarioSummariesLoading: true,
  countyScenarioSummariesModel: null,
  countySummariesLoading: true,
  countySummariesModel: null,
  getScenarioSummaries: () => null,
  getSummaries: () => null,
  marketProvidersEffectivenessLoading: true,
  marketProvidersEfficiencyLoading: true,
  marketProvidersScores: [],
  networkProviderScenarioEffectivenessScores: [],
  networkProviderScenarioEffectivenessScoresLoading: true,
  networkProviderScenarioEfficiencyScores: [],
  networkProviderScenarioEfficiencyScoresLoading: true,
  networkProvidersEffectivenessLoading: true,
  networkProvidersEfficiencyLoading: true,
  networkProvidersScores: [],
  onApplyFiltersClick: () => null,
  onDashboardDetailsViewClick: () => null,
  onSummaryDashboardDetailsViewClick: () => null,
  prospectProvidersEffectivenessLoading: true,
  prospectProvidersEfficiencyLoading: true,
  prospectProvidersScores: [],
  setShowDashboardDetails: () => null,
  showDashboardDetails: false,
  specialtyScenarioSummariesLoading: true,
  specialtyScenarioSummariesModel: null,
  specialtySummariesLoading: true,
  specialtySummariesModel: null,
}

export const ClaimsInsightsDashboardContext = createContext(initialValues);

const ClaimsInsightsDashboardContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [countyScenarioSummariesLoading, setCountyScenarioSummariesLoading] = useState<boolean>(initialValues.countyScenarioSummariesLoading);
  const [countyScenarioSummariesModel, setCountyScenarioSummariesModel] = useState<ClaimsInsightsSummaryScenarioModel>(initialValues.countyScenarioSummariesModel);
  const [countySummariesLoading, setCountySummariesLoading] = useState<boolean>(initialValues.countySummariesLoading);
  const [countySummariesModel, setCountySummariesModel] = useState<ClaimsInsightsScoreSummaryModel>(initialValues.countySummariesModel);
  const [marketProvidersEffectivenessLoading, setMarketProvidersEffectivenessLoading] = useState<boolean>(initialValues.marketProvidersEffectivenessLoading);
  const [marketProvidersEfficiencyLoading, setMarketProvidersEfficiencyLoading] = useState<boolean>(initialValues.marketProvidersEfficiencyLoading);
  const [marketProvidersScores, setMarketProvidersScores] = useState<ClaimsInsightsScore[]>(initialValues.marketProvidersScores);
  const [networkProviderScenarioEffectivenessScores, setNetworkProviderScenarioEffectivenessScores] = useState<ClaimsInsightsProviderScenarioResult[]>(initialValues.networkProviderScenarioEffectivenessScores);
  const [networkProviderScenarioEffectivenessScoresLoading, setNetworkProviderScenarioEffectivenessScoresLoading] = useState<boolean>(initialValues.networkProviderScenarioEffectivenessScoresLoading);
  const [networkProviderScenarioEfficiencyScores, setNetworkProviderScenarioEfficiencyScores] = useState<ClaimsInsightsProviderScenarioResult[]>(initialValues.networkProviderScenarioEfficiencyScores);
  const [networkProviderScenarioEfficiencyScoresLoading, setNetworkProviderScenarioEfficiencyScoresLoading] = useState<boolean>(initialValues.networkProviderScenarioEfficiencyScoresLoading);
  const [networkProvidersEffectivenessLoading, setNetworkProvidersEffectivenessLoading] = useState<boolean>(initialValues.networkProvidersEffectivenessLoading);
  const [networkProvidersEfficiencyLoading, setNetworkProvidersEfficiencyLoading] = useState<boolean>(initialValues.networkProvidersEfficiencyLoading);
  const [networkProvidersScores, setNetworkProvidersScores] = useState<ClaimsInsightsScore[]>(initialValues.networkProvidersScores);
  const [prospectProvidersEffectivenessLoading, setProspectProvidersEffectivenessLoading] = useState<boolean>(initialValues.prospectProvidersEffectivenessLoading);
  const [prospectProvidersEfficiencyLoading, setProspectProvidersEfficiencyLoading] = useState<boolean>(initialValues.prospectProvidersEfficiencyLoading);
  const [prospectProvidersScores, setProspectProvidersScores] = useState<ClaimsInsightsScore[]>(initialValues.prospectProvidersScores);
  const [showDashboardDetails, setShowDashboardDetails] = useState<boolean>(initialValues.showDashboardDetails);
  const [specialtyScenarioSummariesLoading, setSpecialtyScenarioSummariesLoading] = useState<boolean>(initialValues.specialtyScenarioSummariesLoading);
  const [specialtyScenarioSummariesModel, setSpecialtyScenarioSummariesModel] = useState<ClaimsInsightsSummaryScenarioModel>(initialValues.specialtyScenarioSummariesModel);
  const [specialtySummariesLoading, setSpecialtySummariesLoading] = useState<boolean>(initialValues.specialtySummariesLoading);
  const [specialtySummariesModel, setSpecialtySummariesModel] = useState<ClaimsInsightsScoreSummaryModel>(initialValues.specialtySummariesModel);
  const [error, setError] = useState("");

  const pciDashboardService = new PciDashboardService();
  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);
  const projectContext = useContext(CountyProjectContext);

  const formatProviderScenarioWidgetData = (scores: ClaimsInsightsProviderScenarioResult[]): ClaimsInsightsProviderScenarioResult[] => {
    // provider widget data should always have all possible score types present in their data
    // if data does not exist, create it
    // create a new array in order to always display the score types in the same order
    const newScores: ClaimsInsightsProviderScenarioResult[] = [];

    for (const category of Object.values(ClaimsInsightsScoreTypeEnum)) {
      let score: ClaimsInsightsProviderScenarioResult = scores.find(x => x.category === category);

      if (!score) {
        score = {
          category,
          categoryCount: null,
          changeCount: null,
          compareCategoryCount: null,
        };
      }

      newScores.push(score);
    }

    return newScores;
  }

  const formatProviderWidgetData = (scores: ClaimsInsightsScore[]): ClaimsInsightsScore[] => {
    // provider widget data should always have all possible score types present in their data
    // if data does not exist, create it
    // create a new array in order to always display the score types in the same order
    const newScores: ClaimsInsightsScore[] = [];

    for (const scoreType of Object.values(ClaimsInsightsScoreTypeEnum)) {
      let score: ClaimsInsightsScore = scores.find(x => x.scoreType === scoreType);

      if (!score) {
        score = {
          effectiveness: null,
          efficiency: null,
          scoreType,
        };
      }

      newScores.push(score);
    }

    return newScores;
  }

  const getProviderScenarioWidgetData = (filtersPayload: PciDashboardFiltersPayload): void => {
    filtersPayload.providerNetworkType = ProviderNetworkTypeEnum.Network;

    for (const rankingRequestType of Object.values(ClaimsInsightsRankingRequestTypeEnum)) {
      filtersPayload.rankingRequestType = rankingRequestType;

      pciDashboardService.getClaimsInsightsProviderScenarioWidgetData(
        projectContext.project.projectId,
        pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId,
        filtersPayload
      ).then((data) => {
        data = formatProviderScenarioWidgetData(data);

        switch (rankingRequestType) {
          case ClaimsInsightsRankingRequestTypeEnum.Effectiveness:
            setNetworkProviderScenarioEffectivenessScores(data);
            setNetworkProviderScenarioEffectivenessScoresLoading(false);
            break;
          case ClaimsInsightsRankingRequestTypeEnum.Efficiency:
            setNetworkProviderScenarioEfficiencyScores(data);
            setNetworkProviderScenarioEfficiencyScoresLoading(false);
            break;
          default:
            break;
        }
      });
    }
  }

  const getProviderWidgetData = (filtersPayload: PciDashboardFiltersPayload): void => {
    const providerNetworkTypes: ProviderNetworkTypeEnum[] = [ProviderNetworkTypeEnum.Market, ProviderNetworkTypeEnum.Network, ProviderNetworkTypeEnum.Prospect];

    for (const providerNetworkType of providerNetworkTypes) {
      filtersPayload.providerNetworkType = providerNetworkType;

      let scores: ClaimsInsightsScore[] = [];

      for (const rankingRequestType of Object.values(ClaimsInsightsRankingRequestTypeEnum)) {
        filtersPayload.rankingRequestType = rankingRequestType;

        pciDashboardService.getClaimsInsightsProviderWidgetData(
          projectContext.project.projectId,
          pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId,
          filtersPayload
        ).then((data) => {

          if (scores.length === 0) {
            for (const datum of data) {
              const score: ClaimsInsightsScore = {
                effectiveness: rankingRequestType === ClaimsInsightsRankingRequestTypeEnum.Effectiveness ? datum.categoryCount : null,
                efficiency: rankingRequestType === ClaimsInsightsRankingRequestTypeEnum.Efficiency ? datum.categoryCount : null,
                scoreType: datum.category,
              };

              scores.push(score);
            }
          } else {
            for (const datum of data) {
              const score = scores.find(x => x.scoreType === datum.category);

              if (!!score) {
                switch (rankingRequestType) {
                  case ClaimsInsightsRankingRequestTypeEnum.Effectiveness:
                    score.effectiveness = datum.categoryCount;
                    break;
                  case ClaimsInsightsRankingRequestTypeEnum.Efficiency:
                    score.efficiency = datum.categoryCount;
                    break;
                  default:
                    break;
                }
              }
            }
          }

          scores = formatProviderWidgetData(scores);

          switch (providerNetworkType) {
            case ProviderNetworkTypeEnum.Market:
              setMarketProvidersScores(scores);
              rankingRequestType === ClaimsInsightsRankingRequestTypeEnum.Effectiveness ?
                setMarketProvidersEffectivenessLoading(false) :
                setMarketProvidersEfficiencyLoading(false);
              break;
            case ProviderNetworkTypeEnum.Network:
              setNetworkProvidersScores(scores);
              rankingRequestType === ClaimsInsightsRankingRequestTypeEnum.Effectiveness ?
                setNetworkProvidersEffectivenessLoading(false) :
                setNetworkProvidersEfficiencyLoading(false);
              break;
            case ProviderNetworkTypeEnum.Prospect:
              setProspectProvidersScores(scores);
              rankingRequestType === ClaimsInsightsRankingRequestTypeEnum.Effectiveness ?
                setProspectProvidersEffectivenessLoading(false) :
                setProspectProvidersEfficiencyLoading(false);
              break;
            default:
              break;
          }
        }, (error) => {
          setError(error);
        });
      }
    }
  }

  const getScenarioSummaries = (
    filtersPayload: PciDashboardFiltersPayload,
    summaryType: PciDashboardSummaryType,
    sortedColumn: string = null,
    sortDirection: SortDirection = null,
    isSorting?: boolean,
    isFiltering?: boolean,
  ): void => {
    if (!filtersPayload) {
      filtersPayload = cloneDeep(pciDashboardFiltersContext.displayedFilters);

      // if accessing the cached model due to all of the counties, states, and specialties being selected,
      // display all those filters as selected in the filters applied accordion, but pass empty arrays to the api
      if (filtersPayload.countySSAs.length === pciDashboardFiltersContext.counties.length) {
        filtersPayload.countySSAs = [];
      }

      if (filtersPayload.specialtyGroupCodes.length === pciDashboardFiltersContext.specialties.length) {
        filtersPayload.specialtyGroupCodes = [];
      }

      if (!filtersPayload.countyStatuses.includes(RegionSetStatusType.Opportunity) &&
        filtersPayload.countyStatuses.length === pciDashboardFiltersContext.countyStatuses.filter(x => x.key !== RegionSetStatusType.Opportunity).length) {
        filtersPayload.countyStatuses = [];
      }
    }

    if (sortDirection === SortDirection.None || sortDirection === SortDirection.Unknown) {
      // this occurs when we're undoing a sort
      sortedColumn = null;
    }

    filtersPayload.sortedColumn = sortedColumn;
    filtersPayload.sortDirection = sortDirection;

    switch (summaryType) {
      case PciDashboardSummaryType.County:
        filtersPayload.take = isSorting ? countyScenarioSummariesModel.summaries.length : ((!countyScenarioSummariesModel || isFiltering) ? 10 : countyScenarioSummariesModel.totalCount);
        filtersPayload.skip = (isSorting || isFiltering || !countyScenarioSummariesModel) ? 0 : countyScenarioSummariesModel.summaries.length;

        pciDashboardService.getClaimsInsightsScenarioCountySummaryWidgetData(
          projectContext.project.projectId,
          pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId,
          filtersPayload
        ).then((data) => {
          if (isSorting || isFiltering || !countyScenarioSummariesModel) {
            setCountyScenarioSummariesModel(data);
          } else {
            const newModel: ClaimsInsightsSummaryScenarioModel = cloneDeep(countyScenarioSummariesModel);
            newModel.summaries = newModel.summaries.concat(data.summaries);
            setCountyScenarioSummariesModel(newModel);
          }

          setCountyScenarioSummariesLoading(false);
        }, (error) => {
          setCountyScenarioSummariesLoading(false);
          setError(error);
        })
        break;
      case PciDashboardSummaryType.Specialty:
        filtersPayload.take = isSorting ? specialtyScenarioSummariesModel.summaries.length : ((!specialtyScenarioSummariesModel || isFiltering) ? 10 : specialtyScenarioSummariesModel.totalCount);
        filtersPayload.skip = (isSorting || isFiltering || !specialtyScenarioSummariesModel) ? 0 : specialtyScenarioSummariesModel.summaries.length;

        pciDashboardService.getClaimsInsightsScenarioSpecialtySummaryWidgetData(
          projectContext.project.projectId,
          pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId,
          filtersPayload
        ).then((data) => {
          if (isSorting || isFiltering || !specialtyScenarioSummariesModel) {
            setSpecialtyScenarioSummariesModel(data);
          } else {
            const newModel: ClaimsInsightsSummaryScenarioModel = cloneDeep(specialtyScenarioSummariesModel);
            newModel.summaries = newModel.summaries.concat(data.summaries);
            setSpecialtyScenarioSummariesModel(newModel);
          }

          setSpecialtyScenarioSummariesLoading(false);
        }, (error) => {
          setSpecialtyScenarioSummariesLoading(false);
          setError(error);
        })
        break;
      default:
        break;
    }
  }

  const getSummaries = (
    filtersPayload: PciDashboardFiltersPayload,
    summaryType: PciDashboardSummaryType,
    sortedColumn: string = null,
    sortDirection: SortDirection = null,
    isSorting?: boolean,
    isFiltering?: boolean,
  ): void => {
    if (!filtersPayload) {
      filtersPayload = cloneDeep(pciDashboardFiltersContext.displayedFilters);

      // if accessing the cached model due to all of the counties, states, and specialties being selected,
      // display all those filters as selected in the filters applied accordion, but pass empty arrays to the api
      if (filtersPayload.countySSAs.length === pciDashboardFiltersContext.counties.length) {
        filtersPayload.countySSAs = [];
      }

      if (filtersPayload.specialtyGroupCodes.length === pciDashboardFiltersContext.specialties.length) {
        filtersPayload.specialtyGroupCodes = [];
      }

      if (!filtersPayload.countyStatuses.includes(RegionSetStatusType.Opportunity) &&
        filtersPayload.countyStatuses.length === pciDashboardFiltersContext.countyStatuses.filter(x => x.key !== RegionSetStatusType.Opportunity).length) {
        filtersPayload.countyStatuses = [];
      }
    }
      
    if (sortDirection === SortDirection.None || sortDirection === SortDirection.Unknown) {
      // this occurs when we're undoing a sort
      sortedColumn = null;
    }

    filtersPayload.sortedColumn = sortedColumn;
    filtersPayload.sortDirection = sortDirection;

    switch (summaryType) {
      case PciDashboardSummaryType.County:
        filtersPayload.take = isSorting ? countySummariesModel.summaries.length : ((!countySummariesModel || isFiltering) ? 10 : countySummariesModel.totalCount);
        filtersPayload.skip = (isSorting || isFiltering || !countySummariesModel) ? 0 : countySummariesModel.summaries.length;

        pciDashboardService.getClaimsInsightsCountySummaryWidgetData(
          projectContext.project.projectId,
          pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId,
          filtersPayload
        ).then((data) => {
          if (isSorting || isFiltering || !countySummariesModel) {
            setCountySummariesModel(data);
          } else {
            const newModel: ClaimsInsightsScoreSummaryModel = cloneDeep(countySummariesModel);
            newModel.summaries = newModel.summaries.concat(data.summaries);
            setCountySummariesModel(newModel);
          }

          setCountySummariesLoading(false);
        }, (error) => {
          setCountySummariesLoading(false);
          setError(error);
        })
        break;
      case PciDashboardSummaryType.Specialty:
        filtersPayload.take = isSorting ? specialtySummariesModel.summaries.length : ((!specialtySummariesModel || isFiltering) ? 10 : specialtySummariesModel.totalCount);
        filtersPayload.skip = (isSorting || isFiltering || !specialtySummariesModel) ? 0 : specialtySummariesModel.summaries.length;

        pciDashboardService.getClaimsInsightsSpecialtySummaryWidgetData(
          projectContext.project.projectId,
          pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId,
          filtersPayload
        ).then((data) => {
          if (isSorting || isFiltering || !specialtySummariesModel) {
            setSpecialtySummariesModel(data);
          } else {
            const newModel: ClaimsInsightsScoreSummaryModel = cloneDeep(specialtySummariesModel);
            newModel.summaries = newModel.summaries.concat(data.summaries);
            setSpecialtySummariesModel(newModel);
          }

          setSpecialtySummariesLoading(false);
        }, (error) => {
          setSpecialtySummariesLoading(false);
          setError(error);
        })
        break;
      default:
        break;
    }
  }

  const onApplyFiltersClick = async (filtersPayload: PciDashboardFiltersPayload): Promise<void> => {
    let isFiltering = false;
    if (!countySummariesLoading && !specialtySummariesLoading) {
      isFiltering = true;
      }

    const payload: PciDashboardFiltersPayload = cloneDeep(filtersPayload);

    // if accessing the cached model due to all of the counties, states, and specialties being selected,
    // display all those filters as selected in the filters applied accordion, but pass empty arrays to the api
    if (payload.countySSAs.length === pciDashboardFiltersContext.counties.length) {
      payload.countySSAs = [];
    }

    if (payload.specialtyGroupCodes.length === pciDashboardFiltersContext.specialties.length) {
      payload.specialtyGroupCodes = [];
    }

    if (!payload.countyStatuses.includes(RegionSetStatusType.Opportunity) &&
      payload.countyStatuses.length === pciDashboardFiltersContext.countyStatuses.filter(x => x.key !== RegionSetStatusType.Opportunity).length) {
      payload.countyStatuses = [];
    }

    if (!filtersPayload.compareTimeslices) {
      setCountySummariesLoading(true);
      setMarketProvidersEffectivenessLoading(true);
      setMarketProvidersEfficiencyLoading(true);
      setNetworkProvidersEffectivenessLoading(true);
      setNetworkProvidersEfficiencyLoading(true);
      setProspectProvidersEffectivenessLoading(true);
      setProspectProvidersEfficiencyLoading(true);
      setSpecialtySummariesLoading(true);
      getProviderWidgetData(payload);
      getSummaries(payload, PciDashboardSummaryType.County, null, null, null, isFiltering);
      getSummaries(payload, PciDashboardSummaryType.Specialty, null, null, null, isFiltering);
    } else {
      setNetworkProviderScenarioEffectivenessScoresLoading(true);
      setNetworkProviderScenarioEfficiencyScoresLoading(true);
      setCountyScenarioSummariesLoading(true);
      setSpecialtyScenarioSummariesLoading(true);
      getProviderScenarioWidgetData(payload);
      getScenarioSummaries(payload, PciDashboardSummaryType.County, null, null, null, isFiltering);
      getScenarioSummaries(payload, PciDashboardSummaryType.Specialty, null, null, null, isFiltering);
    }
  }

  const onDashboardDetailsViewClick = (
    insightsCategory: ClaimsInsightsScoreTypeEnum,
    providerNetworkType: ProviderNetworkTypeEnum,
    rankingRequestType: ClaimsInsightsRankingRequestTypeEnum,
    scenario?: PciDashboardScenarioTypeEnum,
  ) => {
    const filters: PciDashboardFiltersPayload = cloneDeep(pciDashboardFiltersContext.displayedFilters);
    filters.insightsCategory = insightsCategory;
    filters.providerNetworkType = providerNetworkType;
    filters.rankingRequestType = rankingRequestType;
    filters.scenario = scenario;
    pciDashboardFiltersContext.setDisplayedFilters(filters);
    setShowDashboardDetails(true);
  }

  const onSummaryDashboardDetailsViewClick = (
    dashboardDetailsParam: string,
    summaryType: PciDashboardSummaryType,
    scenario?: PciDashboardScenarioTypeEnum,
  ) => {
    const filters: PciDashboardFiltersPayload = cloneDeep(pciDashboardFiltersContext.displayedFilters);
    filters.providerNetworkType = ProviderNetworkTypeEnum.Network;
    filters.scenario = scenario;

    switch (summaryType) {
      case PciDashboardSummaryType.County:
        const county: PciDashboardCounty = pciDashboardFiltersContext.counties.find(x => x.countyName === dashboardDetailsParam);

        if (!!county) {
          filters.countySSAs = [county.countySSA];
        }

        break;
      case PciDashboardSummaryType.Specialty:
        filters.specialtyGroupCodes = [dashboardDetailsParam];
        break;
      default:
        break;
    }

    pciDashboardFiltersContext.setDisplayedFilters(filters);
    setShowDashboardDetails(true);
  }

  useEffect(() => {
    if (!!error) {
      throw error;
    }
  }, [error]);

  return (
    <ClaimsInsightsDashboardContext.Provider
      value={{
        countyScenarioSummariesLoading,
        countyScenarioSummariesModel,
        countySummariesLoading,
        countySummariesModel,
        getScenarioSummaries,
        getSummaries,
        marketProvidersEffectivenessLoading,
        marketProvidersEfficiencyLoading,
        marketProvidersScores,
        networkProviderScenarioEffectivenessScores,
        networkProviderScenarioEffectivenessScoresLoading,
        networkProviderScenarioEfficiencyScores,
        networkProviderScenarioEfficiencyScoresLoading,
        networkProvidersEffectivenessLoading,
        networkProvidersEfficiencyLoading,
        networkProvidersScores,
        onApplyFiltersClick,
        onDashboardDetailsViewClick,
        onSummaryDashboardDetailsViewClick,
        prospectProvidersEffectivenessLoading,
        prospectProvidersEfficiencyLoading,
        prospectProvidersScores,
        setShowDashboardDetails,
        showDashboardDetails,
        specialtyScenarioSummariesLoading,
        specialtyScenarioSummariesModel,
        specialtySummariesLoading,
        specialtySummariesModel,
      }}
    >
      {props.children}
    </ClaimsInsightsDashboardContext.Provider>
  )
}

export default ClaimsInsightsDashboardContextProvider;